import React, { useState, useEffect, useRef } from 'react';

const BRAND = {
  cyan: '#00AEEF',
  magenta: '#EC008C',
  red: '#ED1C24',
};

const CONTACT_CARDS = [
  {
    label: 'Call Us',
    color: BRAND.cyan,
    icon: (
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    ),
    lines: ['+91 97028 50777', '+91 97025 66777'],
    href: 'tel:+919702850777',
  },
  {
    label: 'Project Location',
    color: BRAND.magenta,
    icon: (
      <>
        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
        <circle cx="12" cy="10" r="3" />
      </>
    ),
    lines: ['Shirdhon & Khoni', 'Kalyan, Maharashtra'],
  },
  {
    label: 'Cheque in favour of',
    color: BRAND.red,
    icon: (
      <>
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
        <line x1="6" y1="16" x2="6.01" y2="16" />
        <line x1="10" y1="16" x2="14" y2="16" />
      </>
    ),
    lines: ['Sai Enforcement And Security Pvt. Ltd.'],
  },
];

function ContactInfoCard({ card, delay, visible }) {
  const inner = (
    <div className="relative h-full rounded-[0.9rem] bg-white p-6 transition-all duration-300 group-hover:shadow-md">
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
        style={{ background: `${card.color}14` }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={card.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {card.icon}
        </svg>
      </div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{card.label}</p>
      {card.lines.map((line, i) => (
        <p
          key={line}
          className={`font-bold text-slate-900 leading-snug ${i === 0 ? 'text-lg' : 'text-sm text-slate-600 font-medium'}`}
        >
          {line}
        </p>
      ))}
    </div>
  );

  return (
    <div
      className={`about-reveal group ${visible ? 'about-visible' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="relative h-full rounded-2xl p-[2px] ses-brand-gradient transition-all duration-500 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/80">
        {card.href ? (
          <a href={card.href} className="block h-full">
            {inner}
          </a>
        ) : (
          inner
        )}
      </div>
    </div>
  );
}

const inputClass =
  'w-full px-4 py-3.5 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-transparent focus:ring-2 transition-all';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [visible, setVisible] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const pageRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.06 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const whatsappNumber = '919702850777';
    const text = `Hello Sai Enforcement And Security Pvt. Ltd.!%0A%0A*Name:* ${formData.name}%0A*Email:* ${formData.email}%0A*Phone:* ${formData.phone}%0A*Message:* ${formData.message}`;
    window.open(`https://wa.me/${whatsappNumber}?text=${text}`, '_blank');
  };

  const focusRing = (field) =>
    focusedField === field ? { boxShadow: `0 0 0 2px ${BRAND.cyan}55` } : {};

  return (
    <div
      ref={pageRef}
      className="w-full min-h-screen font-display overflow-x-hidden"
    >
      {/* Page background */}
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-white via-slate-50 to-white pointer-events-none" />
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-[#00AEEF]/6 via-transparent to-[#EC008C]/6 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto pt-28 pb-20 lg:pt-36 lg:pb-28 px-4 md:px-10">
        <div className="absolute top-0 left-0 right-0 h-1 ses-brand-gradient opacity-60" />

        {/* Header */}
        <div className={`about-reveal text-center mb-12 md:mb-14 ${visible ? 'about-visible' : ''}`}>
          <span className="mb-5 inline-flex items-center gap-2 rounded-full p-[1.5px] ses-brand-gradient shadow-sm">
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={BRAND.cyan} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Contact Us
            </span>
          </span>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight">
            Get in{' '}
            <span className="ses-brand-text">Touch</span>
          </h1>
          <p className="mt-4 text-slate-500 max-w-xl mx-auto leading-relaxed">
            Have a question about MHADA properties or need help with financing? Our team is ready
            to assist you.
          </p>
          <div className="mx-auto mt-6 h-1 w-24 rounded-full ses-brand-gradient opacity-80" />
        </div>

        {/* Contact info cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10 md:mb-12">
          {CONTACT_CARDS.map((card, i) => (
            <ContactInfoCard key={card.label} card={card} delay={150 + i * 100} visible={visible} />
          ))}
        </div>

        {/* Form + Map */}
        <div
          className={`about-reveal rounded-[1.75rem] p-[2px] ses-brand-gradient shadow-xl ${visible ? 'about-visible' : ''}`}
          style={{ transitionDelay: '400ms' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-[1.65rem] bg-white overflow-hidden">
            {/* Form */}
            <div className="p-8 md:p-10 lg:p-12">
              <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-2">
                Send us a{' '}
                <span className="ses-brand-text">message</span>
              </h3>
              <p className="text-sm text-slate-500 mb-8">
                Fill in the form and we&apos;ll connect with you on WhatsApp instantly.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name</label>
                  <input
                    required
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    className={inputClass}
                    style={focusRing('name')}
                    placeholder="Your full name"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address</label>
                    <input
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      className={inputClass}
                      style={focusRing('email')}
                      placeholder="you@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number</label>
                    <input
                      required
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('phone')}
                      onBlur={() => setFocusedField(null)}
                      className={inputClass}
                      style={focusRing('phone')}
                      placeholder="+91 97028 50777"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Message</label>
                  <textarea
                    required
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField(null)}
                    rows="4"
                    className={`${inputClass} resize-none`}
                    style={focusRing('message')}
                    placeholder="How can we help you?"
                  />
                </div>

                <button
                  type="submit"
                  className="relative w-full overflow-hidden group py-4 rounded-2xl text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,174,239,0.35)] flex items-center justify-center gap-2"
                >
                  <span className="absolute inset-0 ses-brand-gradient opacity-90 group-hover:opacity-100 transition-opacity" />
                  <span className="relative">Send via WhatsApp</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="relative">
                    <path d="M22 2 11 13" />
                    <path d="M22 2 15 22 4 9 9 4 22 2" />
                  </svg>
                </button>
              </form>
            </div>

            {/* Map */}
            <div className="relative min-h-[380px] lg:min-h-full bg-slate-100">
              <div className="absolute inset-3 lg:inset-4 rounded-2xl overflow-hidden border border-slate-200 shadow-inner">
                <iframe
                  title="Shirdhon & Khoni Kalyan Project Location Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d120562.11585802377!2d73.06456056637375!3d19.24033054170366!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be795d19cbbfced%3A0xc3c6b2da75df115a!2sKalyan%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                  className="absolute inset-0 w-full h-full border-0"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              {/* Map overlay badge */}
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
                <div className="rounded-full px-4 py-2 bg-white/95 backdrop-blur-md border border-slate-200 shadow-lg flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00AEEF] opacity-60" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00AEEF]" />
                  </span>
                  <span className="text-xs font-bold text-slate-700">Kalyan, Maharashtra</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
